import { useRuntimeConfig } from '#app'
import { CraftUtil } from '../util/craft-util'

// TODO: implement sentry
export function useGraphQL() {
    const runtimeConfig = useRuntimeConfig()
    const nuxtApp = useNuxtApp()

    let config = {
        endpoint: '',
        token: '',
    }

    let cookieCredentials = null 
    if (import.meta.dev) {
        cookieCredentials = useCookie('cms_credentials')
    } else {
        cookieCredentials = useCookie('cms_credentials', {
            sameSite: 'none',
            secure: true,
        })
    }

    if (import.meta.server) {
        const headers = nuxtApp.ssrContext.event.node.req.headers
        config = CraftUtil.getServerAuthConfig(runtimeConfig, headers)

        cookieCredentials.value = { ...config }
    }

    if (import.meta.client) {
        // Get the endpoint and token from the 'cms_credentials' cookie
        if (cookieCredentials.value !== undefined) {
            config.endpoint = cookieCredentials.value.endpoint
            config.token = cookieCredentials.value.token
        }
    }

    if (!config.endpoint || !config.token) {
        throw createError({
            statusCode: 500,
            statusMessage: 'GraphQL endpoint or token missing.',
        })
    }

    const adminUrl = replace(config.endpoint, '/api', '/admin')
    const craftUrl = replace(config.endpoint, '/api', '')

    const query = async ({ query, variables = {}, ...options }) => {
        const { isPreview, previewToken } = usePreview()

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${config.token}`
        }

        if (isPreview) {
            headers['X-Craft-Token'] = previewToken.value
        }

        const beforeFetchStamp = Date.now() / 1000

        const queryOptions = {
            key: options?.key,
            method: 'POST',
            headers,
            body: JSON.stringify({
                query,
                variables
            }),
        }

        // if server = false, then only run this with the client side $fetch to avoid request issues with useFetch
        if (options.server === false) {
            try {
                const response = await $fetch(config.endpoint, queryOptions)
                // Just log GraphQL errors because GraphQL can sometimes recover from it
                if (response?.errors) {
                    console.error('GraphQL Error:', response?.errors?.[0].message)
                }

                return response.data
            }
            catch {
                throw createError({
                    statusCode: 500,
                    statusMessage: 'Something went wrong',
                })
            }
        }

        // use useFetch for server side requests
        const { data, error } = await useFetch(config.endpoint, queryOptions)

        // if error is defined then its an unrecoverable server error
        if (error.value) {
            throw createError({
                statusCode: 500,
                statusMessage: 'Fetch error',
            })
        }

        // Just log GraphQL errors because GraphQL can sometimes recover from it
        if (data.value?.errors) {
            console.error('GraphQL Error:', data.value?.errors?.[0].message)
        }

        // debug loggin (todo: send to sentry)
        console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
        if (import.meta.client) {
            console.log(query)
            console.log(variables)
        }
        console.groupEnd()

        return data.value?.data
    }

    const cmsQuery = async ({ uri, variables = {}, ...options }) => {
        const { isPreview, previewToken, previewTimestamp } = usePreview()

        const endpoint = replace(config.endpoint, '/api', '/data-api')

        const headers = {
            'Authorization': `Bearer ${config.token}`,
            'Cache-Control': 'public'
        }

        if (isPreview.value) {
            headers['X-Craft-Token'] = previewToken.value

            // Needed for cache busting in preview mode
            const separator = uri.includes('?') ? '&' : '?'
            uri += `${separator}previewTimestamp=${previewTimestamp.value}`
        } else {
            // allow cache busting
            const route = useRoute()

            if (route.query && 'no-cache' in route.query) {
                // Add no-cache parameter using proper separator
                const separator = uri.includes('?') ? '&' : '?'
                uri += `${separator}no-cache=${Date.now()}`
            }
        }

        const beforeFetchStamp = Date.now() / 1000

        // if server = false, then only run this with the client side $fetch to avoid request issues with useFetch
        if (options.server === false) {
            try {
                const response = await $fetch(`${endpoint}/${uri}`, {
                    ...options,
                    headers
                })
                // Just log GraphQL errors because GraphQL can sometimes recover from it
                if (response?.errors) {
                    console.error('CMS Data Request Error:', response?.errors?.[0].message)
                }

                return {data: response.data}
            }
            catch {
                throw createError({
                    statusCode: 500,
                    statusMessage: 'Something went wrong',
                })
            }
        }

        const { data, error } = await useFetch(`${endpoint}/${uri}`, {
            ...options,
            headers,
        })

        // if error is defined then its an unrecoverable server error
        if (error.value) {
            throw createError({
                statusCode: 500,
                statusMessage: 'Something went wrong',
                data: {
                    message: error.value,
                },
            })
        }

        // Just log GraphQL errors because GraphQL can sometimes recover from it
        if (data.value?.errors) {
            console.error('CMS Request Error:', data.value?.errors?.[0].message)
        }

        // debug loggin (todo: send to sentry)
        console.groupCollapsed(`${options.key} query in`, `${(Date.now() / 1000 - beforeFetchStamp).toFixed(2)}s`)
        if (import.meta.client) {
            console.log(query)
            console.log(variables)
        }
        console.groupEnd()

        return { data }
    }

    return {
        apiUrl: config.endpoint,
        adminUrl,
        craftUrl,
        query,
        cmsQuery
    }
}
